// Simple reactive store for super_admin active club context.
// When set, all API requests include the x-club-id header. NOTE: the backend
// does NOT derive the tenant from it — data scoping is driven by X-Tenant-Id,
// so every club switch must also call setTenantId with the club's tenant.

import type { QueryClient } from '@tanstack/react-query';
import { setTenantId } from './tenantStore';

let activeClubId: string | null = sessionStorage.getItem('sa-club-id');
const listeners = new Set<() => void>();

export function getSuperAdminClubId(): string | null {
  return activeClubId;
}

export function setSuperAdminClubId(id: string | null): void {
  activeClubId = id;
  if (id) {
    sessionStorage.setItem('sa-club-id', id);
  } else {
    sessionStorage.removeItem('sa-club-id');
  }
  listeners.forEach((fn) => fn());
}

export function subscribeSuperAdminClub(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Activate a club context for a super admin — the single switch path shared by
 * ChooseClubPage, the sidebar ClubSwitcher, and the Super Admin panel. Sets the
 * club (x-club-id) and its tenant (X-Tenant-Id) together, then CLEARS the query
 * cache: a tenant switch must never leave another club's data on screen, and an
 * empty cache makes every page fall back to its loading state while the new
 * club's data arrives.
 */
export function activateClubContext(clubId: string, tenantId: string, queryClient: QueryClient): void {
  setSuperAdminClubId(clubId);
  setTenantId(tenantId);
  queryClient.clear();
}
